import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import { handleImagePaste } from '../utils/pasteFiles';
import ComplaintUnitPicker from './ComplaintUnitPicker.jsx';
import { COMPLAINT_CATEGORIES } from '../utils/complaintCategories';

const API_BASE = process.env.REACT_APP_API_URL;

const SUPPLIERS = [
  { value: 'gpv', label: 'GPV' },
  { value: 'ubiqconn', label: 'Ubiqconn' },
  { value: 'acmepoint', label: 'Acmepoint' },
  { value: 'hk_ringer', label: 'HK Ringer' },
];

const CATEGORY_ICONS = {
  assembly_defect:    '🔧',
  parts_compliance:   '📦',
  functional_failure: '⚡',
  delivery_issue:     '🚚',
  documentation:      '📄',
  customer_escalation:'👤',
};

export default function SubmitComplaintModal({ onClose, onCreated, prefill = {}, linkedRma = null }) {
  const { getToken } = useAuth();

  const [form, setForm] = useState({
    supplier: prefill.supplier || 'gpv',
    escalationLevel: prefill.escalationLevel || 1,
    title: prefill.title || '',
    complaintTypes: prefill.complaintTypes || [],
    productNumber: prefill.productNumber || '',
    serialNumber: prefill.serialNumber || '',
    batchNumber: prefill.batchNumber || '',
    productionOrder: prefill.productionOrder || '',
    quantity: prefill.quantity || '',
    affectedValue: prefill.affectedValue || '',
    description: prefill.description || '',
    notifyEmails: prefill.notifyEmails || '',
    internalNotes: prefill.internalNotes || '',
    hoursSpent: prefill.hoursSpent || '',
    hourlyRate: prefill.hourlyRate || '',
    bomCost: prefill.bomCost || '',
    currency: prefill.currency || 'EUR',
  });

  // Which top-level categories are expanded
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [files, setFiles] = useState([]);
  // The units this complaint covers, looked up by service tag (issue #748).
  const [units, setUnits] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Who the supplier-side notification will actually reach, resolved by the
  // same code that sends it — so what is shown here is what will happen.
  const [recipients, setRecipients] = useState(null);

  // Quality document linking
  const [qualityDocs, setQualityDocs] = useState([]);
  const [qualitySearch, setQualitySearch] = useState('');
  const [qualitySearchResults, setQualitySearchResults] = useState([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [selectedQualityDocs, setSelectedQualityDocs] = useState([]);
  const [showQualitySearch, setShowQualitySearch] = useState(false);

  useEffect(() => {
    (async () => {
      setQualityLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/quality/documents?status=Released`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        if (d.ok) { setQualityDocs(d.documents || []); setQualitySearchResults(d.documents || []); }
      } catch { /* ignore */ } finally { setQualityLoading(false); }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    const q = qualitySearch.toLowerCase();
    setQualitySearchResults(
      qualityDocs.filter(d =>
        d.documentNumber.toLowerCase().includes(q) || d.title.toLowerCase().includes(q)
      )
    );
  }, [qualitySearch, qualityDocs]);

  const toggleQualityDoc = (doc) => {
    setSelectedQualityDocs(prev =>
      prev.find(d => d._id === doc._id) ? prev.filter(d => d._id !== doc._id) : [...prev, doc]
    );
  };

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleCategory = (catValue) => {
    setExpandedCategories(prev =>
      prev.includes(catValue)
        ? prev.filter(c => c !== catValue)
        : [...prev, catValue]
    );
  };

  const toggleSubtype = (subtypeValue) => {
    setForm(prev => ({
      ...prev,
      complaintTypes: prev.complaintTypes.includes(subtypeValue)
        ? prev.complaintTypes.filter(t => t !== subtypeValue)
        : [...prev.complaintTypes, subtypeValue]
    }));
  };

  // Count selected subtypes per category
  const selectedInCategory = (cat) =>
    cat.subtypes.filter(s => form.complaintTypes.includes(s.value)).length;

  useEffect(() => {
    if (!form.supplier) { setRecipients(null); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const token = await getToken();
        const qs = form.notifyEmails ? `?notifyEmails=${encodeURIComponent(form.notifyEmails)}` : '';
        const res = await fetch(`${API_BASE}/complaints/notify-recipients/${form.supplier}${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!cancelled && data.ok) setRecipients(data);
      } catch {
        if (!cancelled) setRecipients(null);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.supplier, form.notifyEmails, getToken]);

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.supplier) { setError('Supplier is required.'); return; }

    setSubmitting(true);
    setError('');
    try {
      const token = await getToken();
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          v.forEach(item => body.append(k, item));
        } else {
          body.append(k, v);
        }
      });
      files.forEach(f => body.append('evidence', f));
      if (units.length) body.append('units', JSON.stringify(units));
      if (linkedRma) {
        body.append('linkedRmaId', linkedRma._id);
        body.append('linkedRmaNumber', linkedRma.rmaId);
      }

      const res = await fetch(`${API_BASE}/complaints`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await res.json();
      if (!data.ok) { setError(data.error || 'Failed to create complaint.'); return; }

      // Attach any selected quality documents
      for (const doc of selectedQualityDocs) {
        try {
          await fetch(`${API_BASE}/complaints/${data.complaint._id}/quality-links`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ documentId: doc._id }),
          });
        } catch { /* non-fatal */ }
      }

      onCreated(data.complaint);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      {/* One file field on this form, so a paste anywhere in it is unambiguous
          evidence (issue #739). */}
      <div style={modal} onPaste={e => handleImagePaste(e, setFiles)}>
        <div style={modalHeader}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: colors.textStrong }}>Submit Supplier Complaint</h2>
            {linkedRma && (
              <div style={{ fontSize: '0.72rem', color: colors.primary, marginTop: '0.2rem' }}>
                Escalating from RMA: <strong>{linkedRma.rmaId}</strong>
                {linkedRma.serialNumber ? ` · ${linkedRma.serialNumber}` : ''}
              </div>
            )}
          </div>
          <button onClick={onClose} className="ni-button ni-button--ghost" style={{ padding: '0.25rem 0.6rem' }}>✕</button>
        </div>

        <div style={bodyStyle}>
          <Row>
            <Field label="Supplier *">
              <select value={form.supplier} onChange={e => set('supplier', e.target.value)} className="ni-input">
                {SUPPLIERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Escalation Level">
              <select value={form.escalationLevel} onChange={e => set('escalationLevel', Number(e.target.value))} className="ni-input">
                <option value={1}>Level 1 — Standard</option>
                <option value={2}>Level 2 — Elevated</option>
                <option value={3}>Level 3 — Critical</option>
              </select>
            </Field>
          </Row>

          <Field label="Title *">
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              className="ni-input"
              placeholder="Short descriptive title of the non-conformance"
            />
          </Field>

          {/* ── Category picker ── */}
          <div style={{ marginBottom: '0.75rem' }}>
            <FieldLabel>Complaint Category &amp; Type</FieldLabel>
            {form.complaintTypes.length > 0 && (
              <div style={{ fontSize: '0.72rem', color: colors.textDim, marginTop: '0.2rem' }}>
                {form.complaintTypes.length} type{form.complaintTypes.length > 1 ? 's' : ''} selected
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              {COMPLAINT_CATEGORIES.map(cat => {
                const isOpen = expandedCategories.includes(cat.value);
                const count = selectedInCategory(cat);
                return (
                  <div key={cat.value} style={{
                    border: `1px solid ${count > 0 ? colors.primary : colors.panelBorder}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: count > 0 ? `${colors.primary}08` : colors.surfaceMuted,
                    transition: 'border-color 0.15s',
                  }}>
                    {/* Category header — click to expand */}
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat.value)}
                      style={{
                        width: '100%', background: 'none', border: 'none',
                        cursor: 'pointer', padding: '0.65rem 0.85rem',
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{CATEGORY_ICONS[cat.value]}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: colors.textStrong, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {cat.label}
                          {count > 0 && (
                            <span style={{ fontSize: '0.68rem', background: colors.primary, color: '#000', borderRadius: 8, padding: '0 5px', fontWeight: 700 }}>
                              {count}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: colors.textDim, marginTop: '0.1rem' }}>{cat.description}</div>
                      </div>
                      <span style={{ color: colors.textDim, fontSize: '0.8rem', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>▼</span>
                    </button>

                    {/* Subtypes — shown when expanded */}
                    {isOpen && (
                      <div style={{ borderTop: `1px solid ${colors.panelBorder}`, padding: '0.6rem 0.85rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        {cat.subtypes.map(sub => {
                          const selected = form.complaintTypes.includes(sub.value);
                          return (
                            <button
                              key={sub.value}
                              type="button"
                              onClick={() => toggleSubtype(sub.value)}
                              style={{
                                background: selected ? `${colors.primary}18` : colors.surface,
                                border: `1px solid ${selected ? colors.primary : colors.panelBorder}`,
                                borderRadius: 6,
                                padding: '0.5rem 0.75rem',
                                cursor: 'pointer',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.6rem',
                              }}
                            >
                              <span style={{
                                width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: '0.05rem',
                                background: selected ? colors.primary : 'none',
                                border: `2px solid ${selected ? colors.primary : colors.textDim}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.65rem', color: '#000', fontWeight: 900,
                              }}>
                                {selected ? '✓' : ''}
                              </span>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: selected ? colors.primary : colors.textBody }}>
                                  {sub.label}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: colors.textDim, marginTop: '0.1rem' }}>{sub.hint}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Row>
            <Field label="Product Number">
              <input value={form.productNumber} onChange={e => set('productNumber', e.target.value)} className="ni-input" placeholder="e.g. WE270 000 100" />
            </Field>
            <Field label="Batch / Lot Number">
              <input value={form.batchNumber} onChange={e => set('batchNumber', e.target.value)} className="ni-input" placeholder="Batch number" />
            </Field>
          </Row>

          <Row>
            <Field label="Affected units">
              <ComplaintUnitPicker
                units={units}
                onChange={(next, removed, nextFirst) => {
                  setUnits(next);
                  if (!removed) return;
                  // Only re-point what that unit had filled in. Anything typed
                  // by hand is left alone.
                  setForm(prev => ({
                    ...prev,
                    serialNumber: prev.serialNumber === (removed.serialNumber || removed.serviceTagNumber)
                      ? (nextFirst ? (nextFirst.serialNumber || nextFirst.serviceTagNumber || '') : '')
                      : prev.serialNumber,
                    productNumber: prev.productNumber === removed.productNumber
                      ? (nextFirst ? (nextFirst.productNumber || '') : '')
                      : prev.productNumber,
                  }));
                }}
                onAutofill={unit => {
                  // Fill what the unit already tells us, rather than making
                  // someone copy it from another tab. Anything already typed
                  // wins -- this must not overwrite a deliberate entry.
                  setForm(prev => ({
                    ...prev,
                    serialNumber: prev.serialNumber || unit.serialNumber || '',
                    productNumber: prev.productNumber || unit.productNumber || '',
                  }));
                }}
              />
            </Field>

            <Field label="Serial Number">
              <input value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} className="ni-input" placeholder="Serial number" />
            </Field>
            <Field label="Production Order">
              <input value={form.productionOrder} onChange={e => set('productionOrder', e.target.value)} className="ni-input" placeholder="PO number" />
            </Field>
          </Row>

          <Row>
            <Field label="Quantity Affected">
              <input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} className="ni-input" placeholder="0" min="0" />
            </Field>
            <Field label="Affected Value (EUR)">
              <input type="number" value={form.affectedValue} onChange={e => set('affectedValue', e.target.value)} className="ni-input" placeholder="0.00" min="0" />
            </Field>
          </Row>

          <Field label="Description">
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              className="ni-input"
              rows={4}
              placeholder="Detailed description of the non-conformance..."
              style={{ resize: 'vertical' }}
            />
          </Field>

          <Divider label="Compensation Estimate" />

          <Row>
            <Field label="Hours Spent">
              <input type="number" value={form.hoursSpent} onChange={e => set('hoursSpent', e.target.value)} className="ni-input" placeholder="0" min="0" />
            </Field>
            <Field label="Hourly Rate (EUR)">
              <input type="number" value={form.hourlyRate} onChange={e => set('hourlyRate', e.target.value)} className="ni-input" placeholder="0.00" min="0" />
            </Field>
            <Field label="BOM Cost (EUR)">
              <input type="number" value={form.bomCost} onChange={e => set('bomCost', e.target.value)} className="ni-input" placeholder="0.00" min="0" />
            </Field>
          </Row>

          <Field label="Notify at supplier (optional)">
            <input
              type="text"
              value={form.notifyEmails}
              onChange={e => set('notifyEmails', e.target.value)}
              className="ni-input"
              placeholder="quality@supplier.com, engineer@supplier.com"
            />
            <div style={{ fontSize: '0.72rem', color: colors.textDim, marginTop: '0.25rem' }}>
              Who receives the link and the updates. Leave blank to use the active
              contacts held for this supplier in the Supplier Matrix.
            </div>
            {recipients && (
              <div style={{
                marginTop: '0.4rem',
                fontSize: '0.75rem',
                padding: '0.45rem 0.6rem',
                borderRadius: 6,
                background: recipients.recipients.length ? 'rgba(70,187,137,0.10)' : 'rgba(251,191,36,0.10)',
                border: `1px solid ${recipients.recipients.length ? 'rgba(70,187,137,0.30)' : 'rgba(251,191,36,0.35)'}`,
                color: recipients.recipients.length ? colors.primary : '#fbbf24',
              }}>
                {recipients.recipients.length ? (
                  <>
                    Will notify <strong>{recipients.recipients.join(', ')}</strong>
                    {recipients.source === 'matrix'
                      ? ` — from ${recipients.supplierName || 'the Supplier Matrix'}`
                      : ' — entered above'}
                  </>
                ) : (
                  <>No contacts on file for this supplier, so nothing will be sent. Add them in
                  the Supplier Matrix, or type addresses above.</>
                )}
              </div>
            )}
          </Field>

          <Field label="Internal Notes (not visible to supplier)">
            <textarea
              value={form.internalNotes}
              onChange={e => set('internalNotes', e.target.value)}
              className="ni-input"
              rows={2}
              placeholder="Internal notes..."
              style={{ resize: 'vertical' }}
            />
          </Field>

          <Divider label="Quality Documents" />

          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <FieldLabel>Linked Quality Procedures</FieldLabel>
              <button
                type="button"
                onClick={() => setShowQualitySearch(v => !v)}
                style={{ background: 'none', border: `1px solid ${colors.panelBorder}`, color: colors.primary, borderRadius: 5, padding: '2px 10px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}
              >
                {showQualitySearch ? 'Hide' : '+ Add'}
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: colors.textDim, marginBottom: '0.5rem' }}>
              Attach the quality procedures this complaint relates to. Content will be frozen as a snapshot.
            </div>

            {selectedQualityDocs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                {selectedQualityDocs.map(doc => (
                  <div key={doc._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `${colors.primary}10`, border: `1px solid ${colors.primary}40`, borderRadius: 6, padding: '6px 10px' }}>
                    <div>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.primary, fontWeight: 700 }}>{doc.documentNumber}</span>
                      <span style={{ fontSize: '0.75rem', color: colors.textBody, marginLeft: 8 }}>{doc.title}</span>
                      <span style={{ fontSize: '0.68rem', color: colors.textMuted, marginLeft: 6 }}>Rev. {doc.currentRevision}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleQualityDoc(doc)}
                      style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '0.8rem', padding: '0 4px' }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            {showQualitySearch && (
              <div style={{ border: `1px solid ${colors.panelBorder}`, borderRadius: 8, overflow: 'hidden', background: colors.surfaceMuted }}>
                <div style={{ padding: '8px 10px', borderBottom: `1px solid ${colors.panelBorder}` }}>
                  <input
                    autoFocus
                    value={qualitySearch}
                    onChange={e => setQualitySearch(e.target.value)}
                    placeholder="Search by document number or title…"
                    className="ni-input"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {qualityLoading ? (
                    <div style={{ padding: 16, textAlign: 'center', color: colors.textMuted, fontSize: '0.78rem' }}>Loading…</div>
                  ) : qualitySearchResults.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: colors.textMuted, fontSize: '0.78rem' }}>No released documents found.</div>
                  ) : qualitySearchResults.map(doc => {
                    const isSelected = selectedQualityDocs.some(d => d._id === doc._id);
                    return (
                      <div
                        key={doc._id}
                        onClick={() => toggleQualityDoc(doc)}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                          background: isSelected ? `${colors.primary}15` : 'transparent',
                          borderLeft: isSelected ? `3px solid ${colors.primary}` : '3px solid transparent',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.73rem', color: colors.primary, fontWeight: 700 }}>{doc.documentNumber}</span>
                            <span style={{ fontSize: '0.68rem', color: colors.textDim }}>{doc.level}</span>
                          </div>
                          <div style={{ fontSize: '0.78rem', color: colors.textBody, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</div>
                        </div>
                        <span style={{ fontSize: '0.68rem', color: colors.textMuted, fontFamily: 'monospace', flexShrink: 0 }}>Rev. {doc.currentRevision}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <Field label="Evidence Files">
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip"
              onChange={e => setFiles(Array.from(e.target.files))}
              className="ni-input"
              style={{ padding: '0.4rem' }}
            />
            <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginTop: '0.25rem' }}>
              {files.length > 0
                ? `${files.length} file(s) selected — ${files.map(f => f.name).join(', ')}`
                : 'Or paste a screenshot straight in (Ctrl+V).'}
            </div>
          </Field>

          {error && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: '0.5rem 0', background: `${colors.danger}15`, padding: '0.5rem 0.75rem', borderRadius: 6 }}>{error}</p>}
        </div>

        <div style={footer}>
          <button onClick={onClose} className="ni-button ni-button--ghost" disabled={submitting}>Cancel</button>
          <button onClick={handleSubmit} className="ni-button ni-button--primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Complaint'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>{children}</div>;
}

function FieldLabel({ children }) {
  return <div style={{ fontSize: '0.72rem', color: colors.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.25rem 0 0.75rem' }}>
      <div style={{ flex: 1, height: 1, background: colors.panelBorder }} />
      <span style={{ fontSize: '0.68rem', color: colors.textDim, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: colors.panelBorder }} />
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  overflowY: 'auto', padding: '2rem 1rem',
};

const modal = {
  background: colors.surface,
  border: `1px solid ${colors.panelBorder}`,
  borderRadius: 12,
  width: '100%', maxWidth: 680,
  display: 'flex', flexDirection: 'column',
  boxShadow: colors.shadow,
};

const modalHeader = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '1rem 1.25rem',
  borderBottom: `1px solid ${colors.panelBorder}`,
};

const bodyStyle = {
  padding: '1.25rem',
  overflowY: 'auto',
  maxHeight: '75vh',
};

const footer = {
  display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
  padding: '1rem 1.25rem',
  borderTop: `1px solid ${colors.panelBorder}`,
};
